// Coupons for the marketing site (`cupones/{CODIGO}`, superadmin only).
// The contract (types, validity rule, form schema, share link) lives in
// @taller/shared; here: the form as the person types it (strings, whole
// Santiago days) mapped onto that schema, and the list helpers.
import {
  esquemaCupon,
  estadoCupon,
  usoDe,
  type Cupon,
  type FormularioCupon,
  type TipoCupon,
  type UsoCupon,
} from '@taller/shared';
import { z } from 'zod';
import { aMillis, type ConId, type MarcaTiempo } from '@/datos/tipos';
import { diaIso, inicioDiaIso, sumarDiasIso } from '@/lib/tiempo';

export {
  advertenciaCupon,
  enlaceCupon,
  ETIQUETAS_USO_CUPON,
  MAX_DURACION_CAMPANA,
  MAX_PORCENTAJE_CAMPANA,
  MAX_PORCENTAJE_CUPON,
  RANGO_DURACION_CUPON,
  RANGO_VALOR_CUPON,
  USO_INTERNO_CUPON,
  estadoCupon,
  ETIQUETAS_ESTADO_CUPON,
  ETIQUETAS_TIPO_CUPON,
  REGEX_CODIGO_CUPON,
  normalizarCodigoCupon,
  TIPOS_CUPON,
  USOS_CUPON,
  usoDe,
  type EstadoCupon,
  type TipoCupon,
  type UsoCupon,
} from '@taller/shared';

/**
 * As read: `creadoEn` is null while the server timestamp is pending, and a
 * coupon stored before `uso` existed has none (read it with `usoDe`).
 */
export type CuponDoc = ConId<
  Omit<Cupon<MarcaTiempo>, 'creadoEn' | 'uso'> & { creadoEn: MarcaTiempo | null; uso?: UsoCupon }
>;

// ---- Labels ----

const meses = (n: number) => (n === 1 ? '1 mes' : `${n} meses`);

/**
 * "15 % por 3 meses", "20 % de descuento" (a coupon stored before
 * duracionMeses existed), "1 mes gratis", "3 meses gratis".
 */
export function textoBeneficio(tipo: string, valor: number, duracionMeses?: number): string {
  if (tipo === 'porcentaje') {
    return duracionMeses ? `${valor} % por ${meses(duracionMeses)}` : `${valor} % de descuento`;
  }
  if (tipo === 'meses_gratis') return `${meses(valor)} gratis`;
  return `${tipo} ${valor}`;
}

/** Internal coupons are applied by hand in the commercial record, never on the site. */
export const esInterno = (c: Pick<CuponDoc, 'uso' | 'tipo' | 'valor' | 'duracionMeses'>) =>
  usoDe(c) === 'interno';

/**
 * Months the benefit lasts. Coupons created before the field existed: free
 * months last their own value, a percentage one month.
 */
export const duracionDe = (c: Pick<CuponDoc, 'tipo' | 'valor'> & { duracionMeses?: number }) =>
  c.duracionMeses ?? (c.tipo === 'meses_gratis' ? c.valor : 1);

/**
 * Validity as whole Santiago days. `vigenteHasta` is exclusive (shared
 * `estadoCupon`), so the last valid day is the one before it.
 */
export function diasVigencia(c: Pick<CuponDoc, 'vigenteDesde' | 'vigenteHasta'>) {
  const desde = aMillis(c.vigenteDesde);
  const hasta = aMillis(c.vigenteHasta);
  return {
    desde: desde === null ? null : diaIso(desde),
    hasta: hasta === null ? null : diaIso(hasta - 1),
  };
}

/** Newest first; one still being written (no date yet) goes on top. */
export function ordenarCupones(lista: readonly CuponDoc[]): CuponDoc[] {
  const t = (c: CuponDoc) => aMillis(c.creadoEn) ?? Number.POSITIVE_INFINITY;
  return [...lista].sort((a, b) => t(b) - t(a) || a.codigo.localeCompare(b.codigo));
}

/** Can a visitor use it right now? */
export const esVigente = (c: CuponDoc, ahora = Date.now()) => estadoCupon(c, ahora) === 'vigente';

// ---- Form ----

/**
 * The form holds text (inputs) and whole days ("2026-10-01"). It maps them
 * onto the shared schema and reports its issues on the same fields, so the
 * rules (lengths, value per type, validity order) are written only once.
 */
const textoFormulario = {
  descripcion: z.string(),
  vigenteDesde: z.string(),
  vigenteHasta: z.string(),
  usosMax: z.string(),
};
export type EdicionCupon = { [K in keyof typeof textoFormulario]: string };
export type NuevoCupon = EdicionCupon & {
  codigo: string;
  /** Campaign or internal; a campaign is always a percentage. */
  uso: string;
  tipo: string;
  valor: string;
  /** Only asked for `porcentaje`; free months last their own value. */
  duracionMeses: string;
};

const DIA = /^\d{4}-\d{2}-\d{2}$/;
const dia = (v: string) => (DIA.test(v.trim()) ? v.trim() : undefined);
const numero = (v: string) => (v.trim() === '' ? Number.NaN : Number(v.trim()));

/** Form strings → the shared FormularioCupon shape (may still be invalid). */
function aFormulario(v: NuevoCupon, activo: boolean): Record<keyof FormularioCupon, unknown> {
  const desde = dia(v.vigenteDesde);
  const hasta = dia(v.vigenteHasta);
  // A campaign has no type field: it is always a percentage.
  const tipo = v.uso === 'campana' ? 'porcentaje' : v.tipo;
  return {
    codigo: v.codigo,
    descripcion: v.descripcion,
    uso: v.uso,
    tipo,
    valor: numero(v.valor),
    duracionMeses: tipo === 'meses_gratis' ? numero(v.valor) : numero(v.duracionMeses),
    activo,
    vigenteDesde: desde ? new Date(inicioDiaIso(desde)) : undefined,
    // Exclusive end: the first instant after the chosen last day.
    vigenteHasta: hasta ? new Date(inicioDiaIso(sumarDiasIso(hasta, 1))) : undefined,
    usosMax: v.usosMax.trim() === '' ? undefined : numero(v.usosMax),
  };
}

function validar(v: NuevoCupon, activo: boolean, ctx: z.RefinementCtx) {
  for (const [campo, texto] of [
    ['vigenteDesde', v.vigenteDesde],
    ['vigenteHasta', v.vigenteHasta],
  ] as const) {
    if (texto.trim() && !dia(texto)) {
      ctx.addIssue({ code: 'custom', message: 'Usa una fecha válida.', path: [campo] });
    }
  }
  const r = esquemaCupon.safeParse(aFormulario(v, activo));
  if (r.success) return;
  const vistos = new Set<string>();
  for (const i of r.error.issues) {
    const campo = String(i.path[0] ?? 'descripcion');
    if (vistos.has(campo)) continue; // one message per field
    vistos.add(campo);
    ctx.addIssue({ code: 'custom', message: i.message, path: [campo] });
  }
}

export const esquemaNuevoCupon = z
  .object({
    codigo: z.string(),
    uso: z.string(),
    tipo: z.string(),
    valor: z.string(),
    duracionMeses: z.string(),
    ...textoFormulario,
  })
  .superRefine((v, ctx) => validar(v, true, ctx));

type Fijos = Pick<CuponDoc, 'codigo' | 'tipo' | 'valor' | 'uso'> & { duracionMeses?: number };

/**
 * The benefit (code, use, type, value, duration) never changes after
 * creation. A coupon stored before `uso` gets the one `usoDe` reads.
 */
const fijos = (c: Fijos) => ({
  codigo: c.codigo,
  uso: usoDe(c),
  tipo: c.tipo,
  valor: String(c.valor),
  duracionMeses: String(duracionDe(c)),
});

/** Editing keeps the benefit; the cap cannot go below the uses so far. */
export function esquemaEdicionCupon(c: Fijos & Pick<CuponDoc, 'activo' | 'usos'>) {
  return z.object(textoFormulario).superRefine((v, ctx) => {
    validar({ ...v, ...fijos(c) }, c.activo, ctx);
    const max = numero(v.usosMax);
    if (Number.isInteger(max) && max >= 1 && max < c.usos) {
      ctx.addIssue({
        code: 'custom',
        message: `Ya se aplicó ${c.usos} ${c.usos === 1 ? 'vez' : 'veces'}: el máximo no puede ser menor.`,
        path: ['usosMax'],
      });
    }
  });
}

/** Validity and cap as stored: instants (ms) or null when left empty. */
export type LimitesCupon = {
  vigenteDesde: number | null;
  vigenteHasta: number | null;
  usosMax: number | null;
};

export type DatosNuevoCupon = LimitesCupon & {
  codigo: string;
  descripcion: string;
  uso: UsoCupon;
  tipo: TipoCupon;
  valor: number;
  duracionMeses: number;
};
/** `uso` is written on every edit so a coupon stored before it gains one. */
export type CambiosCupon = LimitesCupon & { descripcion: string; uso: UsoCupon };

function parsear(v: NuevoCupon, activo: boolean): FormularioCupon {
  return esquemaCupon.parse(aFormulario(v, activo));
}

const limites = (p: FormularioCupon): LimitesCupon => ({
  vigenteDesde: p.vigenteDesde?.getTime() ?? null,
  vigenteHasta: p.vigenteHasta?.getTime() ?? null,
  usosMax: p.usosMax ?? null,
});

export function aCuponNuevo(v: NuevoCupon): DatosNuevoCupon {
  const p = parsear(v, true);
  return {
    codigo: p.codigo,
    descripcion: p.descripcion,
    uso: p.uso,
    tipo: p.tipo,
    valor: p.valor,
    duracionMeses: p.duracionMeses,
    ...limites(p),
  };
}

export function aCambiosCupon(c: CuponDoc, v: EdicionCupon): CambiosCupon {
  const p = parsear({ ...v, ...fijos(c) }, c.activo);
  return { descripcion: p.descripcion, uso: p.uso, ...limites(p) };
}

/** Form values for editing an existing coupon. */
export function valoresEdicion(c: CuponDoc): EdicionCupon {
  const { desde, hasta } = diasVigencia(c);
  return {
    descripcion: c.descripcion,
    vigenteDesde: desde ?? '',
    vigenteHasta: hasta ?? '',
    usosMax: typeof c.usosMax === 'number' ? String(c.usosMax) : '',
  };
}

/** Thrown by `crearCupon` when the id is taken (shown on the code field). */
export class CuponExistente extends Error {
  constructor(codigo: string) {
    super(`Ya existe un cupón con el código ${codigo}. Elige otro.`);
    this.name = 'CuponExistente';
  }
}
